import React from 'react';
import { Box, Grid, Typography, Paper, Stepper, Step, StepLabel, StepContent, useTheme, alpha } from '@mui/material';
import SectionContainer from './SectionContainer';
import AnimatedElement from './AnimatedElement';
import LaptopIcon from '@mui/icons-material/Laptop';
import SettingsIcon from '@mui/icons-material/Settings';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GroupsIcon from '@mui/icons-material/Groups';

const ServiceDescription = () => {
  const theme = useTheme();

  const steps = [
    {
      label: 'Регистрация организатора',
      description: 'Организатор вебинара регистрируется через специальный Telegram-бот и получает доступ к панели управления.',
      icon: <LaptopIcon />,
    },
    {
      label: 'Настройка вебинара',
      description: 'Организатор настраивает параметры вебинара: название, ссылку на YouTube-трансляцию, загружает презентацию и настраивает кнопки призыва к действию.',
      icon: <SettingsIcon />,
    },
    {
      label: 'Приглашение зрителей',
      description: 'После настройки бот генерирует уникальную ссылку, которую организатор может отправить потенциальным зрителям.',
      icon: <SendIcon />,
    },
    {
      label: 'Проведение вебинара',
      description: 'В назначенное время организатор запускает трансляцию и управляет процессом: переключает слайды, активирует кнопки призыва к действию.',
      icon: <PlayArrowIcon />,
    },
    {
      label: 'Аналитика и конверсии',
      description: 'После завершения вебинара организатор получает полную статистику посещений и действий зрителей для дальнейшей работы с аудиторией.',
      icon: <GroupsIcon />,
    },
  ];

  return (
    <SectionContainer
      id="service-description"
      title="Как это работает"
      subtitle="Процесс проведения вебинара через наш сервис прост и интуитивно понятен как для организаторов, так и для зрителей"
      bgColor={alpha(theme.palette.primary.main, 0.03)}
    >
      <Grid container spacing={6} alignItems="center">
        <Grid item xs={12} md={6}>
          <AnimatedElement animation="fadeIn" delay={0.2}>
            <Paper 
              elevation={0}
              sx={{ 
                borderRadius: 4, 
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                mb: { xs: 4, md: 0 },
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: '8px',
                  backgroundColor: theme.palette.telegram.main,
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                }
              }}
            >
              <Box sx={{ p: 4 }}>
                <Typography variant="h5" component="h3" color="telegram.main" fontWeight="bold" gutterBottom>
                  Для организатора
                </Typography>
                
                <Stepper orientation="vertical" sx={{ mt: 3 }}>
                  {steps.map((step, index) => (
                    <Step active key={index} sx={{ mb: 3 }}>
                      <StepLabel 
                        StepIconProps={{ 
                          icon: step.icon,
                          sx: { 
                            color: theme.palette.telegram.main,
                          } 
                        }}
                      >
                        <Typography variant="h6" component="span" fontWeight="bold">
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body1" color="text.secondary">
                          {step.description}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Paper>
          </AnimatedElement>
        </Grid>

        <Grid item xs={12} md={6}>
          <AnimatedElement animation="fadeIn" delay={0.4}>
            <Paper 
              elevation={0}
              sx={{ 
                borderRadius: 4, 
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: '8px',
                  backgroundColor: theme.palette.secondary.main,
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                }
              }}
            >
              <Box sx={{ p: 4 }}>
                <Typography variant="h5" component="h3" color="secondary.main" fontWeight="bold" gutterBottom>
                  Для зрителя
                </Typography>
                
                <Box sx={{ mt: 3 }}>
                  <AnimatedElement animation="fadeInUp" delay={0.5}>
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        1. Получение ссылки
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Зритель получает ссылку на вебинар от организатора через любой канал коммуникации.
                      </Typography>
                    </Box>
                  </AnimatedElement>
                  
                  <AnimatedElement animation="fadeInUp" delay={0.6}>
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        2. Переход в Telegram-бот
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        По ссылке зритель переходит в специальный Telegram-бот для просмотра вебинара.
                      </Typography>
                    </Box>
                  </AnimatedElement>
                  
                  <AnimatedElement animation="fadeInUp" delay={0.7}>
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        3. Просмотр вебинара
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        В назначенное время в интерфейсе бота открывается веб-приложение с трансляцией и презентацией.
                      </Typography>
                    </Box>
                  </AnimatedElement>
                  
                  <AnimatedElement animation="fadeInUp" delay={0.8}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        4. Взаимодействие
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Зритель просматривает трансляцию, видит синхронизированные слайды и может взаимодействовать с кнопками призыва к действию.
                      </Typography>
                    </Box>
                  </AnimatedElement>
                </Box>
              </Box>
            </Paper>
          </AnimatedElement>
        </Grid>
      </Grid>
      
      <AnimatedElement animation="fadeIn" delay={1}>
        <Box sx={{ mt: 10, p: 4, bgcolor: alpha(theme.palette.telegram.main, 0.1), borderRadius: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="telegram.dark" fontWeight="bold" gutterBottom>
            Всё, что нужно – это Telegram!
          </Typography>
          <Typography variant="body1">
            Не требуется установка дополнительных приложений или переход в браузер – весь функционал доступен прямо в мессенджере.
          </Typography>
        </Box>
      </AnimatedElement>
    </SectionContainer>
  );
};

export default ServiceDescription; 